import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtTime, usePlayer } from "@/context/PlayerContext";
import { mediaUrl } from "@/lib/api";
import { ShareTrackButton, WhatsAppTrackButton } from "./ShareTrackButton";
import { SocialIcon } from "./SocialIcons";
import { Waveform } from "./Waveform";

const StreamButtons = ({ track, className = "" }) => (
  <div className={`flex flex-wrap gap-3 ${className}`}>
    {track.spotify_url && (
      <a
        href={track.spotify_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="listen-spotify-btn"
        className="btn-ghost-gold !px-5 !py-2.5 text-xs"
      >
        <SocialIcon name="spotify" size={15} /> Listen on Spotify
      </a>
    )}
    {track.apple_url && (
      <a
        href={track.apple_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="listen-apple-btn"
        className="btn-ghost-gold !px-5 !py-2.5 text-xs"
      >
        Listen on Apple Music
      </a>
    )}
  </div>
);

export const TrackModal = () => {
  const { track, modalOpen, setModalOpen, playing, toggle, time, dur, seek } = usePlayer();

  const onSeek = (e) => {
    const v = Number(e.target.value);
    seek(v);
    e.target.style.setProperty("--fill", `${(v / dur) * 100}%`);
  };

  return (
    <AnimatePresence>
      {modalOpen && track && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-navy-950/90 backdrop-blur-xl overflow-y-auto"
          data-testid="full-track-modal"
        >
          <button
            onClick={() => setModalOpen(false)}
            data-testid="modal-close"
            aria-label="Close"
            className="fixed top-5 right-5 z-10 text-slate-400 hover:text-gold-300 transition-colors p-2"
          >
            <X size={26} />
          </button>
          <div className="min-h-full flex items-center justify-center px-5 py-16">
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-10 items-start"
            >
              <div className="relative">
                <div className="absolute -inset-6 rounded-full bg-gold-400/10 blur-3xl" aria-hidden />
                <img
                  src={mediaUrl(track.cover_url)}
                  alt={`${track.title} cover art`}
                  className={`relative w-full aspect-square object-cover rounded-2xl border border-gold-400/30 ${playing ? "animate-glow" : ""}`}
                  data-testid="modal-cover"
                />
              </div>
              <div>
                <p className="eyebrow mb-2">Open Horizon Records</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-slate-50" data-testid="modal-track-title">
                  {track.title}
                </h2>
                <div className="mt-6 flex items-center gap-4">
                  <button
                    onClick={toggle}
                    data-testid="modal-play-toggle"
                    aria-label={playing ? "Pause" : "Play"}
                    className="w-14 h-14 shrink-0 rounded-full flex items-center justify-center text-navy-950 transition-transform hover:scale-105"
                    style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}
                  >
                    {playing ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-1" />}
                  </button>
                  <Waveform playing={playing} bars={36} className="h-16 flex-1" />
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-xs text-slate-400 tabular-nums">{fmtTime(time)}</span>
                  <input
                    type="range"
                    className="seek flex-1"
                    min={0}
                    max={dur}
                    step={0.1}
                    value={time}
                    onChange={onSeek}
                    data-testid="modal-seek"
                    aria-label="Seek"
                  />
                  <span className="text-xs text-slate-400 tabular-nums">{fmtTime(dur)}</span>
                </div>
                <p className="mt-1 text-[11px] tracking-widest uppercase text-gold-400/70">
                  60-second preview — full track on streaming
                </p>
                <StreamButtons track={track} className="mt-5" />
                <div className="mt-3 flex items-center gap-3">
                  <ShareTrackButton track={track} testid="modal-share-btn" />
                  <WhatsAppTrackButton track={track} testid="modal-whatsapp-btn" />
                </div>
                <Tabs defaultValue="lyrics" className="mt-8" data-testid="modal-tabs">
                  <TabsList className="bg-navy-900 border border-gold-400/15">
                    <TabsTrigger value="lyrics" data-testid="tab-lyrics">Lyrics</TabsTrigger>
                    <TabsTrigger value="about" data-testid="tab-about">About This Track</TabsTrigger>
                    <TabsTrigger value="release" data-testid="tab-release">Full Release</TabsTrigger>
                  </TabsList>
                  <TabsContent value="lyrics" className="mt-5 max-h-72 overflow-y-auto pr-3">
                    <p className="whitespace-pre-line text-sm leading-7 text-slate-300 font-light" data-testid="lyrics-content">
                      {track.lyrics || "Lyrics coming soon."}
                    </p>
                  </TabsContent>
                  <TabsContent value="about" className="mt-5">
                    <p className="whitespace-pre-line text-sm leading-7 text-slate-300 font-light" data-testid="about-content">
                      {track.about || "More about this track coming soon."}
                    </p>
                  </TabsContent>
                  <TabsContent value="release" className="mt-5" data-testid="release-content">
                    <dl className="text-sm space-y-3 text-slate-300">
                      <div className="flex justify-between border-b border-white/5 pb-2">
                        <dt className="text-slate-500">Format</dt>
                        <dd>{track.is_single ? "Single" : "Album track"}</dd>
                      </div>
                      <div className="flex justify-between border-b border-white/5 pb-2">
                        <dt className="text-slate-500">Release date</dt>
                        <dd>{track.release_date || "TBA"}</dd>
                      </div>
                      <div className="flex justify-between border-b border-white/5 pb-2">
                        <dt className="text-slate-500">Label</dt>
                        <dd>Open Horizon Records</dd>
                      </div>
                    </dl>
                    <a
                      href={`/music/${track.id}`}
                      onClick={() => setModalOpen(false)}
                      data-testid="modal-release-page-link"
                      className="inline-block mt-4 text-[11px] tracking-[0.2em] uppercase text-gold-300 hover:text-gold-200 transition-colors"
                    >
                      View release page
                    </a>
                    <StreamButtons track={track} className="mt-5" />
                  </TabsContent>
                </Tabs>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
